import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { Flame } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CANDLE_REFRESH_INTERVAL_MS = 30000;

const getCandleStyle = (type) => {
  switch (type) {
    case 'golden':
      return { flame: '#F59E0B', glow: 'rgba(245,158,11,0.5)' };
    case 'bright':
      return { flame: '#FB923C', glow: 'rgba(251,146,60,0.4)' };
    default:
      return { flame: '#FBBF24', glow: 'rgba(251,191,36,0.3)' };
  }
};

const GlobalCandleWall = ({ maxDisplay = 40 }) => {
  const [candles, setCandles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hoveredCandle, setHoveredCandle] = useState(null);

  const fetchCandles = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/candles-global`);
      setCandles(res.data.candles || []);
      setTotalCount(res.data.total || 0);
    } catch (err) {
      logger.error('Error fetching global candles:', err);
    }
  }, []);

  useEffect(() => {
    fetchCandles();
    const interval = setInterval(fetchCandles, CANDLE_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchCandles]);

  const displayCandles = candles.slice(0, maxDisplay);

  if (totalCount === 0 && displayCandles.length === 0) {
    return (
      <section className="py-12 bg-[#0A0A0A]" data-testid="global-candle-wall">
        <div className="px-6 text-center">
          <Flame className="w-8 h-8 text-[#F59E0B] mx-auto mb-3 opacity-60" />
          <h3 className="font-heading text-xl font-bold text-white mb-2">
            Light a Candle of Hope
          </h3>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            Each candle represents a family supported through your generosity. Be the first to light one.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-[#0A0A0A] overflow-hidden" data-testid="global-candle-wall">
      <div className="px-6">
        <div className="text-center mb-8">
          <Flame className="w-7 h-7 text-[#F59E0B] mx-auto mb-2 drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]" />
          <h3 className="font-heading text-xl font-bold text-white mb-2">
            Light a Candle of Hope
          </h3>
          <p className="text-gray-400 text-sm max-w-md mx-auto mb-1">
            Each candle represents a family supported through your generosity.
          </p>
          {totalCount > 0 && (
            <p className="text-[#F59E0B] font-semibold text-lg mt-2" data-testid="candle-total-count">
              {totalCount} {totalCount === 1 ? 'candle' : 'candles'} lit
            </p>
          )}
        </div>

        {/* Candle Grid */}
        <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto" data-testid="candle-grid">
          {displayCandles.map((candle, index) => {
            const { flame: flameColor, glow: glowColor } = getCandleStyle(candle.candle_type);

            return (
              <div
                key={candle.id || index}
                className="relative group cursor-pointer"
                onMouseEnter={() => setHoveredCandle(candle.id)}
                onMouseLeave={() => setHoveredCandle(null)}
                data-testid={`global-candle-${index}`}
              >
                <div 
                  className="w-8 h-10 flex flex-col items-center justify-end candle-flicker"
                  style={{ animationDelay: `${(index * 0.15) % 3}s` }}
                >
                  {/* Flame */}
                  <div
                    className="w-3 h-4 rounded-full mb-0.5"
                    style={{
                      background: `radial-gradient(ellipse at center, ${flameColor}, transparent)`,
                      boxShadow: `0 0 8px ${glowColor}, 0 0 16px ${glowColor}`,
                    }}
                  />
                  {/* Wick */}
                  <div className="w-[2px] h-1.5 bg-gray-600" />
                  {/* Body */}
                  <div className="w-4 h-5 bg-gradient-to-b from-[#F5E6D0] to-[#E8D5B7] rounded-sm" />
                </div>

                {/* Hover Tooltip */}
                {hoveredCandle === candle.id && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white rounded-lg px-3 py-2 shadow-lg text-center z-10 whitespace-nowrap">
                    <p className="text-xs font-semibold text-[#0A0A0A]">
                      {candle.name || 'Anonymous Donor'}
                    </p>
                    <p className="text-[10px] text-[#4B5563]">1 Family Supported</p>
                    {candle.message && (
                      <p className="text-[10px] text-[#4B5563] italic mt-0.5 max-w-[150px] truncate">
                        "{candle.message}"
                      </p>
                    )}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 -mt-1" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {totalCount > maxDisplay && (
          <p className="text-center text-gray-500 text-xs mt-4">
            +{totalCount - maxDisplay} more candles lit across all programs
          </p>
        )}
      </div>
    </section>
  );
};

export default GlobalCandleWall;
